/**
 * LiveTalk — the run's events as a conversation, not a log.
 *
 * The 2026-09-16 walkthrough of a planning run showed a person a wall of
 * `bash ok · ok` lines and asked them to feel like they were watching a
 * colleague work. They could not, because the agent's own words never
 * reached the page. Now they do (a `message` from the sandbox per turn, a
 * `thinking` with its reasoning), and this pane reads them the way the CLI
 * and the desktop feed already read a session:
 *
 *   what the agent SAID      `.ls-said`     prose, rendered as markdown
 *   what it THOUGHT          `.ls-thought`  folded, italic, one caret
 *   what it DID              `.ls-tool`     one mono row per call, its
 *                                            result folded into the same row
 *   what the machine NOTED   `.ls-note`     stage beats, errors, @yagni's
 *                                            own start and end notes
 *
 * Still one stream. Nothing is summarised into a sentence the run did not
 * produce: the prose is the model's, the verb on a tool row comes from the
 * tool's name and nowhere else, and a result that never arrived leaves the
 * row saying so rather than guessing. The pane is bounded to the newest
 * {@link TALK_TURN_LIMIT} turns by default; the run record holds the rest and
 * lifts the bound.
 */
import { useEffect, useRef, type ReactNode } from "react";
import { Link as RouterLink } from "react-router-dom";
import { Loader2 } from "lucide-react";
import type { CodingEvent } from "@yagni/shared";
import { isSteeringNoteEvent } from "@yagni/shared";
import { Markdown } from "../components/Markdown.js";
import { eventLineText } from "./RunSurface.js";
import { steeringNoteHandedRow } from "./workCopy.js";

/** How many turns the pane keeps on screen. Older ones are on the run record. */
export const TALK_TURN_LIMIT = 80;

/* ── the verb on a tool row ──
   The verb comes from the tool the run called, and from nowhere else. A tool
   this map does not know keeps its own name, which is the honest fallback: the
   surface would rather print `rg` than invent "Searching" for a tool that may
   not search. */
export const TOOL_VERB: Record<string, string> = {
  edit: "Editing",
  write: "Editing",
  str_replace: "Editing",
  read_file: "Reading",
  read: "Reading",
  run_command: "Running",
  bash: "Running",
  grep: "Searching",
  search: "Searching",
  create_pr: "Opening the pull request",
};

/** The same tools, once they have finished: "Ran", "Edited", "Read". */
export const TOOL_PAST: Record<string, string> = {
  edit: "Edited",
  write: "Wrote",
  str_replace: "Edited",
  read_file: "Read",
  read: "Read",
  run_command: "Ran",
  bash: "Ran",
  grep: "Searched for",
  search: "Searched for",
  create_pr: "Opened the pull request",
};

export type TalkTurn =
  | {
      kind: "said";
      key: string;
      from: "sandbox" | "yagni";
      text: string;
      at: number;
      /**
       * A beat that reports rather than speaks: a later plan run saying it
       * reused an earlier run's repository reading. It reads quieter, and it
       * can point at that earlier run.
       */
      ghost?: boolean;
      /** The run whose reading was reused, when the beat names one. */
      reusedFromRunId?: string | null;
    }
  | { kind: "thought"; key: string; text: string; at: number }
  | {
      kind: "tool";
      key: string;
      tool: string;
      verb: string;
      object: string | null;
      /** Null until the result arrives. */
      ok: boolean | null;
      /** The result's own summary, when it said more than ok / failed. */
      summary: string | null;
      /** A search that found nothing: neutral, not an error. */
      noMatch?: boolean;
      at: number;
    }
  | { kind: "note"; key: string; text: string; tone: string; at: number }
  /** A person's steering note reached the session (YAG-1127). */
  | { kind: "steer"; key: string; at: number };

/** The tone one note reads in. Derived from the line the log printed. */
export function lineTone(text: string): string {
  if (text.startsWith("error ·")) return "is-err";
  if (/\bfailed\b/.test(text)) return "is-err";
  if (/\bok ·/.test(text)) return "is-ok";
  if (/heartbeat|no heartbeat|stalled/i.test(text)) return "is-warn";
  return "";
}

/** The milestone a message carries, by name, whether or not this bundle knows it. */
function milestoneNameOf(event: CodingEvent): string | null {
  if (event.kind !== "message") return null;
  const milestone = (event as { milestone?: unknown }).milestone;
  return typeof milestone === "string" ? milestone : null;
}

/**
 * Fold the event stream into turns. A `tool_result` lands on the newest
 * unresolved call of the same tool; one with no call to land on becomes a
 * note in its own words. Kinds with nothing to say (usage, a bare thinking
 * tick) fold into nothing.
 */
export function talkTurnsOf(
  events: readonly CodingEvent[],
  limit = TALK_TURN_LIMIT,
): { turns: TalkTurn[]; hidden: number } {
  const turns: TalkTurn[] = [];
  events.forEach((event, index) => {
    const key = `${index}-${event.at}`;
    switch (event.kind) {
      case "message": {
        const text = event.text.trim();
        if (!text) return;
        // A subagent's progress beat keeps the card's clock honest and its
        // "now" line current; it is not a turn of the conversation (YAG-1095).
        if (event.subagent) return;
        // A person's steering note reached the session: one row of its own,
        // at its time, never @yagni speaking (YAG-1127).
        if (isSteeringNoteEvent(event)) {
          turns.push({ kind: "steer", key, at: event.at });
          return;
        }
        if (milestoneNameOf(event) === "reading_reused") {
          const reusedFrom = (event as { reusedFromRunId?: unknown }).reusedFromRunId;
          turns.push({
            kind: "said",
            key,
            from: event.from,
            text,
            at: event.at,
            ghost: true,
            reusedFromRunId: typeof reusedFrom === "string" && reusedFrom ? reusedFrom : null,
          });
          return;
        }
        turns.push({ kind: "said", key, from: event.from, text, at: event.at });
        return;
      }
      case "thinking": {
        const text = event.text?.trim();
        if (text) turns.push({ kind: "thought", key, text, at: event.at });
        return;
      }
      case "tool_call": {
        const object = event.argsSummary.trim();
        turns.push({
          kind: "tool",
          key,
          tool: event.tool,
          verb: TOOL_VERB[event.tool] ?? event.tool,
          object: object || null,
          ok: null,
          summary: null,
          at: event.at,
        });
        return;
      }
      case "tool_result": {
        for (let cursor = turns.length - 1; cursor >= 0; cursor -= 1) {
          const turn = turns[cursor];
          if (turn && turn.kind === "tool" && turn.ok === null && turn.tool === event.tool) {
            const summary = event.summary.trim();
            turn.ok = event.ok;
            turn.noMatch = event.outcome === "no_match";
            // The neutral state word already says it; do not repeat it after the dot.
            turn.summary =
              !turn.noMatch && summary && summary !== (event.ok ? "ok" : "failed") ? summary : null;
            return;
          }
        }
        break;
      }
      default:
        break;
    }
    const text = eventLineText(event)?.replace(/\s+/g, " ").trim();
    if (!text) return;
    turns.push({ kind: "note", key, text, tone: lineTone(text), at: event.at });
  });
  const hidden = Math.max(0, turns.length - limit);
  return { turns: hidden > 0 ? turns.slice(hidden) : turns, hidden };
}

/**
 * The word at the end of a tool row. A call with no result is "running" only
 * while the run is; on a stopped run it is what it is, a call the record
 * never closed.
 */
export function toolStateLabel(ok: boolean | null, live: boolean): string {
  if (ok === null) return live ? "running" : "no result";
  return ok ? "ok" : "failed";
}

/** HH:MM from an epoch-ms stamp. Empty for an event that recorded no time. */
function clockOf(at: number): string {
  const when = new Date(at);
  if (Number.isNaN(when.getTime())) return "";
  return when.toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit", hour12: false });
}

export interface LiveTalkProps {
  events: readonly CodingEvent[];
  /** The agent whose words these are. Null prints a neutral name. */
  workerName: string | null;
  /** Live keeps the pane pinned to the newest turn and washes it green. */
  live: boolean;
  /** How many turns to keep on screen. The run record passes Infinity. */
  limit?: number;
  /**
   * Where another run of this item lives, so a beat that names one (a reused
   * reading) can link to it. Null or absent prints no link.
   */
  linkForRun?: (runId: string) => string | null;
  /**
   * Who spoke at a moment, for a run whose sessions are different Agents' (a
   * one-attempt lane run is Reeve's plan, then Wright's build and fixes). Null
   * or absent names `workerName` (YAG-1079).
   */
  speakerAt?: (at: number) => string | null;
}

export function LiveTalk({
  events,
  workerName,
  live,
  limit = TALK_TURN_LIMIT,
  linkForRun,
  speakerAt,
}: LiveTalkProps): ReactNode {
  const { turns, hidden } = talkTurnsOf(events, limit);

  // The pane follows the newest turn only while the reader is at the bottom.
  // Someone who scrolled up to read what the agent said a minute ago is not
  // yanked back down by the next tool call.
  const paneRef = useRef<HTMLDivElement | null>(null);
  const pinnedRef = useRef(true);
  // Keyed to the newest turn AND its result, so a row growing an `ok` scrolls
  // too, while a re-render that changed nothing does not.
  const last = turns[turns.length - 1];
  const lastKey = last ? `${last.key}:${last.kind === "tool" ? String(last.ok) : ""}` : "";
  useEffect(() => {
    const el = paneRef.current;
    if (!el || !pinnedRef.current) return;
    el.scrollTop = el.scrollHeight;
  }, [lastKey]);
  const onScroll = () => {
    const el = paneRef.current;
    if (!el) return;
    pinnedRef.current = el.scrollHeight - el.scrollTop - el.clientHeight < 90;
  };

  if (turns.length === 0) return null;
  const who = workerName ?? "The agent";

  return (
    <div className="ls-talk" data-testid="live-stream-talk" ref={paneRef} onScroll={onScroll}>
      {hidden > 0 && (
        <div className="ls-talk-earlier" data-testid="live-stream-talk-earlier">
          {hidden} earlier turn{hidden === 1 ? "" : "s"} {hidden === 1 ? "is" : "are"} on the run
          record
        </div>
      )}
      {turns.map((turn, index) => {
        const fresh = live && index === turns.length - 1 ? " is-new" : "";
        switch (turn.kind) {
          case "said": {
            const reusedHref =
              turn.ghost && turn.reusedFromRunId && linkForRun
                ? linkForRun(turn.reusedFromRunId)
                : null;
            return (
              <article
                key={turn.key}
                className={
                  "ls-said" +
                  (turn.from === "yagni" ? " is-yagni" : "") +
                  (turn.ghost ? " is-ghost" : "") +
                  fresh
                }
                data-testid="talk-said"
                data-from={turn.from}
              >
                <div className="ls-said-head">
                  <span className="ls-said-who" data-testid="talk-said-who">
                    {turn.from === "yagni" ? "@yagni" : (speakerAt?.(turn.at) ?? who)}
                  </span>
                  <span className="ls-said-clock">{clockOf(turn.at)}</span>
                </div>
                <Markdown text={turn.text} className="ls-said-body" />
                {reusedHref && (
                  <RouterLink
                    className="ls-link ls-said-link"
                    to={reusedHref}
                    data-testid="talk-reused-link"
                  >
                    Show that reading
                  </RouterLink>
                )}
              </article>
            );
          }
          case "thought":
            return (
              <details key={turn.key} className={"ls-thought" + fresh} data-testid="talk-thought">
                <summary className="ls-thought-head">
                  <span className="ls-chev" aria-hidden />
                  <span className="ls-thought-word">Thinking</span>
                  <span className="ls-said-clock">{clockOf(turn.at)}</span>
                </summary>
                <div className="ls-thought-body">{turn.text}</div>
              </details>
            );
          case "tool":
            return (
              <div
                key={turn.key}
                className={
                  "ls-tool" +
                  (turn.ok === null
                    ? " is-running"
                    : turn.noMatch
                      ? " is-nomatch"
                      : turn.ok
                        ? " is-ok"
                        : " is-err") +
                  fresh
                }
                data-testid="talk-tool"
                data-state={
                  turn.ok === null
                    ? "running"
                    : turn.noMatch
                      ? "no_match"
                      : turn.ok
                        ? "ok"
                        : "failed"
                }
              >
                <span className="ls-tool-clock">{clockOf(turn.at)}</span>
                <span className="ls-tool-verb">{turn.verb}</span>
                {turn.object && <code className="ls-tool-obj">{turn.object}</code>}
                <span className="ls-tool-state">
                  {turn.ok === null && live && (
                    <Loader2 width={10} height={10} className="wk-spin" aria-hidden />
                  )}
                  {turn.noMatch ? "no matches" : toolStateLabel(turn.ok, live)}
                  {turn.summary ? ` · ${turn.summary}` : ""}
                </span>
              </div>
            );
          case "note":
            return (
              <div
                key={turn.key}
                className={"ls-note " + turn.tone + fresh}
                data-testid="talk-note"
              >
                <span className="ls-tool-clock">{clockOf(turn.at)}</span>
                <span className="ls-note-text">{turn.text}</span>
              </div>
            );
          case "steer":
            return (
              <div key={turn.key} className={"ls-note is-steer" + fresh} data-testid="talk-steer">
                <span className="ls-note-text">{steeringNoteHandedRow(clockOf(turn.at))}</span>
              </div>
            );
          default:
            return null;
        }
      })}
    </div>
  );
}
